import Nav from "./components/Nav";
import Section from "./components/Section";

function PortfolioFigure({
  title,
  size,
  image,
  variant = "large",
  frameClassName = "",
}: {
  title: string;
  size: string;
  image: string;
  variant?: "small" | "large";
  frameClassName?: string;
}) {
  const isSmall = variant === "small";

  return (
    <figure className={isSmall ? "w-full" : "w-full"}>
      <div className={["overflow-hidden", frameClassName].join(" ")}>
        <img
          src={image}
          alt={title}
          className="h-full w-full object-contain"
        />
      </div>
      <figcaption className="mx-auto mt-2 max-w-full text-center text-sm text-black/80">
        {title}, {size}
      </figcaption>
    </figure>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-white font-sans text-black">
      <Nav />
      <a id="top"></a>
      {/* Hero */}
      <header className="pt-24">
        <div className="mx-auto max-w-5xl px-8 pt-12 text-center md:px-12">
          <h1 className="mb-2 font-serif text-4xl font-bold tracking-wide text-black md:text-5xl lg:text-6xl">
            Botanical and Wildlife Art
          </h1>
          <p className="mb-12 font-serif text-lg text-black/80 md:text-xl">
            Artist & Ecologist - Mary Anne Young
          </p>
          <div className="overflow-hidden">
            <img
              src="/A_Curious_Flock_Bobolink_web.jpg"
              alt="Botanical and wildlife illustration"
              className="w-full object-contain"
            />
          </div>
        </div>
      </header>

      {/* Portfolio */}
      <Section id="portfolio" title="Portfolio" wide>
        <p className="mx-auto mb-12 max-w-2xl text-center text-sm leading-relaxed text-black/80">
          I work in fineliner pen and watercolour, capturing the fine details and
          intricate patterns of the natural world.
        </p>
        <div className="space-y-12 md:space-y-16">
          {/* Block 1: 2 small left | large right, aligned side-by-side */}
          <div className="grid grid-cols-1 gap-10 md:grid-cols-[340px_1fr] md:gap-x-20 md:items-start">
            {/* Desktop: 2 fixed rows. Large spans both. */}
            <div className="grid gap-12 md:grid-rows-[260px_260px]">
              <PortfolioFigure
                variant="small"
                title="Up in the Trees, Grey Ratsnake"
                size="5×7″"
                image="/Up_in_the_Trees_Ratsnake_web.jpg"
                frameClassName="h-[260px]"
              />
              <PortfolioFigure
                variant="small"
                title="Nosy Nuthatch"
                size="5×5″"
                image="/Nuthatch.png"
                frameClassName="h-[260px]"
              />
            </div>

            <div className="md:row-span-2">
              <PortfolioFigure
                variant="large"
                title="Fen Plants of the Kawarthas"
                size="11×14″"
                image="/Fen_plants_of_kawarthas_web.jpg"
                frameClassName="md:h-[260px] md:min-h-[520px] h-auto"
              />
            </div>
          </div>

          {/* Block 2: large left | 2 small right, aligned side-by-side */}
          <div className="grid grid-cols-1 gap-10 md:grid-cols-[1fr_340px] md:gap-x-20 md:items-start">
            <div>
              <PortfolioFigure
                variant="large"
                title="A Midsummer Meadow"
                size="5×7″"
                image="/Midsummer_Meadow_TallgrassPrairie_web.jpg"
                frameClassName="md:h-[520px] h-auto"
              />
            </div>

            <div className="grid gap-12 md:grid-rows-[260px_260px]">
              <PortfolioFigure
                variant="small"
                title="Five-Lined Skink"
                size="5×7″"
                image="/Skink.jpg"
                frameClassName="h-[260px]"
              />
              <PortfolioFigure
                variant="small"
                title="Red Oak in Fall"
                size="5×7″"
                image="/RedOak.jpg"
                frameClassName="h-[260px]"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* About */}
      <Section id="about" title="About">
        <div className="mx-auto flex max-w-2xl flex-col items-center">
          <div className="mb-8 aspect-[3/4] w-64 overflow-hidden md:w-72">
            <img
              src="/Mary%20Anne%20Young_web.jpg"
              alt="Mary Anne Young"
              className="h-full w-full object-contain"
            />
          </div>
          <p className="max-w-2xl text-left text-sm leading-relaxed text-black/80">
            I am a Landscape Architect, ecologist, and lifelong plant enthusiast
            inspired by the natural world. Raised in an artistic family, I
            exhibited handmade pottery and jewelry at craft shows before
            pursuing a career in the environmental sector. Years of ecological
            fieldwork honed my focus on the intricate details of plants and
            wildlife, and now through my art I aim to share these delicate
            treasures with others.
          </p>
        </div>
      </Section>

      {/* Contact */}
      <Section id="contact" title="Contact">
        <div className="relative mx-auto max-w-2xl">
          <p className="mb-8 text-left text-sm leading-relaxed text-black/80">
            If you're interested in my art, please contact me directly or follow
            me on Instagram for new art and exhibition updates. To purchase
            digital copies or prints please see my Etsy store, for originals and
            commissions send me an email. I'd love to hear from you!
          </p>
          <dl className="space-y-2 text-sm text-black/80">
            <div className="flex gap-3">
              <dt className="w-16 shrink-0">Email:</dt>
              <dd>
                <a
                  href="mailto:info@mayoutdoors.com"
                  className="underline hover:text-black"
                >
                  info@mayoutdoors.com
                </a>
              </dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-16 shrink-0">Instagram:</dt>
              <dd>
                <a
                  href="https://instagram.com/may_outdoors"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-black"
                >
                  @may_outdoors
                </a>
              </dd>
            </div>
            <div className="flex gap-3">
              <dt className="w-16 shrink-0">Etsy:</dt>
              <dd>coming soon</dd>
            </div>
          </dl>
          <div className="absolute -bottom-4 right-0 w-24 opacity-60">
            <img
              src="/2025_Chickadee_web.jpg"
              alt=""
              aria-hidden
              className="w-full"
            />
          </div>
        </div>
      </Section>

      <footer className="border-t border-black/10 py-8 text-center text-xs text-black/60">
        All work © 2025 Mary Anne Young
      </footer>
    </div>
  );
}

export default App;
